import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { currentDateFormat } from '../utils';
import SaveButton from '../components/buttons/SaveButton';
import CancelButton from '../components/buttons/CancelButton';
import Swal from '../messageBox/customSweetAlert';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../redux/loaderAction';
import Loader from '../components/loader/Loader';
import { FTP_AUTH_URL } from '../constant';

const AddFTPCredentials = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state.loader.loading);
  const [currentDate] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState, reset } = useForm();
  const { errors } = formState;

  const onSubmit = async (data) => {
    setSubmitting(true);
    dispatch(setLoading(true));
    try {
      const payload = {
        host_name: String(data.host_name ?? '').trim(),
        username: String(data.username ?? '').trim(),
        password: String(data.password ?? ''),
        ftp_type: String(data.ftp_type ?? '').trim(),
        port_number: Number(data.port_number),
      };

      const response = await axios.post(FTP_AUTH_URL, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const isCreated =
        response?.status === 201 ||
        response?.data?.status === 201 ||
        response?.data?.status === '201';

      if (isCreated) {
        Swal.fire({
          title: 'Success!',
          text: response?.data?.message || 'FTP credentials created successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate('/home/ftp/credentials/list');
        });
        reset();
      } else {
        Swal.fire({
          title: 'Error!',
          text: response?.data?.message || 'Failed to create FTP credentials.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create FTP credentials.';

      if (status === 409) {
        Swal.fire({
          title: 'Duplicate Record',
          text: message || 'FTP credentials already exist for this configuration.',
          icon: 'warning',
          confirmButtonText: 'OK',
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: message,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } finally {
      setSubmitting(false);
      dispatch(setLoading(false));
    }
  };

  return (
    <>
      <section>
        <div className="d-flex justify-content-between form-container">
          <div>
            <h2 className="fs-5 font-weight-600">Add FTP Credentials</h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>
      <div className="container-fluid px-mobile-2 bg-white px-5 py-4">
        <form onSubmit={handleSubmit(onSubmit)} style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="require-field">Host Name</label>
              <input
                type="text"
                className="form-control"
                autoComplete="off"
                {...register('host_name', {
                  required: { value: true, message: 'Host name is required' },
                })}
              />
              {errors?.host_name?.message && (
                <span className="errors">{errors.host_name.message}</span>
              )}
            </div>
            <div className="col-md-6">
              <label className="require-field">FTP Type</label>
              <select
                className="form-select"
                {...register('ftp_type', {
                  required: { value: true, message: 'FTP type is required' },
                })}
              >
                <option value="">Select FTP Type</option>
                <option value="DHS">DHS</option>
                <option value="WayStar">WayStar</option>
              </select>
              {errors?.ftp_type?.message && (
                <span className="errors">{errors.ftp_type.message}</span>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="require-field">Username</label>
              <input
                type="text"
                className="form-control"
                autoComplete="off"
                {...register('username', {
                  required: { value: true, message: 'Username is required' },
                })}
              />
              {errors?.username?.message && (
                <span className="errors">{errors.username.message}</span>
              )}
            </div>
            <div className="col-md-6">
              <label className="require-field">Password</label>
              <input
                type="password"
                className="form-control"
                autoComplete="new-password"
                {...register('password', {
                  required: { value: true, message: 'Password is required' },
                })}
              />
              {errors?.password?.message && (
                <span className="errors">{errors.password.message}</span>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="require-field">Port Number</label>
              <input
                type="number"
                className="form-control"
                autoComplete="off"
                min="1"
                {...register('port_number', {
                  required: { value: true, message: 'Port number is required' },
                  validate: (value) => {
                    const num = Number(value);
                    if (!Number.isFinite(num) || num <= 0) {
                      return 'Port number must be a valid positive number';
                    }
                    return true;
                  },
                })}
              />
              {errors?.port_number?.message && (
                <span className="errors">{errors.port_number.message}</span>
              )}
            </div>
          </div>
          <div
            className="d-flex gap-2 justify-content-center mt-4 w-100"
            style={{ justifyContent: 'center' }}
          >
            <SaveButton
              type="submit"
              buttonTitle="ADD FTP CREDENTIALS"
              loading={submitting}
              loadingText="Saving..."
            />
            <CancelButton cancelHandler={() => reset()} />
          </div>
        </form>
      </div>
      {loading && <Loader />}
    </>
  );
};

export default AddFTPCredentials;
